<template>
  <v-row v-if="showEnable" no-gutters>
    <v-col>
      <v-text-field
        v-model="fieldValue"
        :variant="variant"
        :label="showLabel"
        :disabled="isDisconnected"
        :loading="isLoading"
        :readonly="isReadOnly"
        type="string"
        inputmode="numeric"
        :suffix="'&nbsp;&nbsp;&nbsp;' + showUnit"
        :error-messages="errorMsg"
        class="me-auto"
        @keydown="onKeyDown"
        @blur="onBlur"
      />
    </v-col>
    <ConfigInputButtons
      :default-value="defaultValue"
      :is-loading="isLoading"
      :is-reload-disabled="isReloadDisabled"
      :is-set-default-disabled="isSetDefaultDisabled"
      :parameter="parameter"
      :show-buttons="showButtons"
      @reload-value="reloadValue"
      @set-default="setDefault"
    />
  </v-row>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import ConfigInputButtons from "../ConfigInputButtons.vue";
import { useBcd } from "@/composables/useBcd";
import { useByteUtils } from "@/composables/useByteUtils";
import { useRounding } from "@/composables/useRounding";
import { ParamField } from "@/enums/ParamField";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { ModbusParam } from "@/types/ModbusParam";
import { TimerId } from "@/types/TimerId";

const {
  parameter,
  showButtons,
  showGroup,
  significance,
  modbusReload,
  fullNumber = false,
  versionNumber = false,
} = defineProps<{
  parameter: ModbusParam;
  showButtons?: boolean;
  showGroup?: boolean;
  significance: number;
  modbusReload: boolean;
  fullNumber?: boolean;
  versionNumber?: boolean;
}>();

const {
  bcdWord32ToDecArray,
  decArrayToBcdWord32,
  decArrayToVersionString,
  versionStringToDecArray,
} = useBcd();
const modbusStore = useModbusStore();
const { roundExponential } = useRounding(significance);
const {
  bytes_to_float,
  bytes_to_uint32,
  uint16_to_bytes,
  bytes_to_int32,
  uint32_to_bytes,
  float_to_bytes,
  int32_to_bytes,
} = useByteUtils();

let unit_conv_enable = false;
let unit_conv_org = "";
let unit_conv_now = "";
let reloadCount = 0;
let writeCount = 0;
let writeValueArray: number[] = [];

const modbusReqTimer = ref<TimerId>(0);
const fieldValue = ref<string | number>(0);
const prevValue = ref<string | number>();

const variant = computed(() => {
  return isReadOnly.value ? undefined : "underlined";
});

const isLoading = computed<boolean>(() => {
  const paramAddr = parameter[ParamField.mbusAddress];
  const nextParamAddr = String(parseInt(paramAddr) + 1);

  if (!(paramAddr in modbusStore.modbusAddrReadBusy)) {
    return false;
  }
  if (parameter.type === "uint16_t") {
    return modbusStore.modbusAddrReadBusy[paramAddr];
  } else {
    return (
      !!modbusStore.modbusAddrReadBusy[paramAddr] ||
      !!modbusStore.modbusAddrReadBusy[nextParamAddr]
    );
  }
});

const errorMsg = computed(() => {
  const paramAddr = parameter[ParamField.mbusAddress];
  const addrErrMsg = modbusStore.modbusAddrErrMsg[paramAddr];

  if (parameter.type === "uint16_t") {
    return addrErrMsg;
  } else if (addrErrMsg !== "") {
    return addrErrMsg;
  } else {
    const nextParamAddr = String(parseInt(paramAddr) + 1);
    return modbusStore.modbusAddrErrMsg[nextParamAddr]; // could be empty or not
  }
});

const showLabel = computed(() => {
  if (!showGroup) {
    return parameter.name;
  } else if (parameter.sub_group === "0" || !parameter.sub_group) {
    return `${parameter.name} ( ${parameter.group} ) `;
  } else {
    return `${parameter.name} ( ${parameter.group}  / ${parameter.sub_group} ) `;
  }
});

const showEnable = computed<boolean>(() => {
  if (
    parameter.type !== "float" &&
    parameter.type !== "int" &&
    parameter.type !== "uint16_t" &&
    parameter.type !== "uint32_t"
  ) {
    return false;
  }
  return modbusStore.isParamUserReadable(parameter);
});

const defaultValue = computed<number>(() => {
  return parseInt(parameter.default);
});

const isDisconnected = computed<boolean>(
  () => modbusStore.portState === PortState.disconnected,
);

const isReadOnly = computed(() => !modbusStore.isParamUserWriteable(parameter));

const isReloadDisabled = computed<boolean>(() => isDefault.value);

const isSetDefaultDisabled = computed<boolean>(
  () => isDefault.value || isReadOnly.value,
);

const isDefault = computed(() => defaultValue.value === fieldValue.value);

const modbusAddrReadBusy = computed(
  () => modbusStore.modbusAddrReadBusy[parameter[ParamField.mbusAddress]],
);

const modbusAddrWriteBusy = computed(
  () => modbusStore.modbusAddrWriteBusy[parameter[ParamField.mbusAddress]],
);

const showUnit = computed<string>(() => {
  const unit = parameter[ParamField.unit];
  if (parseInt(unit) === 0) {
    return "";
  } else if (parseInt(unit) < 0) {
    return unit;
  } else if (unit in modbusStore.modbusRefUnit) {
    return modbusStore.modbusRefUnit[unit];
  } else {
    return "";
  }
});

function updateValue() {
  getValue();
  prevValue.value = fieldValue.value;
}

function getValue() {
  let value_org: string | number = "0";
  if (parameter.type === "uint16_t") {
    value_org = modbusStore.modbusValues[parameter[ParamField.mbusAddress]];
  } else {
    const regH = modbusStore.modbusValues[parameter[ParamField.mbusAddress]];
    const regL =
      modbusStore.modbusValues[parseInt(parameter[ParamField.mbusAddress]) + 1];
    const regH_bytes = uint16_to_bytes(regH);
    const regL_bytes = uint16_to_bytes(regL);

    if (parameter.type === "uint32_t") {
      value_org = bytes_to_uint32([
        regH_bytes[0],
        regH_bytes[1],
        regL_bytes[0],
        regL_bytes[1],
      ]);
    } else if (parameter.type === "int") {
      value_org = bytes_to_int32([
        regH_bytes[0],
        regH_bytes[1],
        regL_bytes[0],
        regL_bytes[1],
      ]);
    } else if (parameter.type === "float") {
      value_org = bytes_to_float([
        regH_bytes[0],
        regH_bytes[1],
        regL_bytes[0],
        regL_bytes[1],
      ]);
    }
  }

  unit_conv_enable = false;
  unit_conv_org = "";
  unit_conv_now = "";

  if (
    parseInt(parameter[ParamField.unit]) <= 0 ||
    !(parameter[ParamField.unit] in modbusStore.modbusRefUnit)
  ) {
    fieldValue.value = value_org;
  } else {
    const to_unit_info = modbusStore.guiUnits.find(
      (item) =>
        item.Symbol == modbusStore.modbusRefUnit[parameter[ParamField.unit]],
    );
    const from_unit_symbol = modbusStore.deviceUnits.find(
      (item) => item.unit_type === to_unit_info?.unit_type,
    );
    let from_unit_info;
    if (from_unit_symbol?.unit_type === "Temperature") {
      from_unit_info = modbusStore.guiUnits.find(
        (item) => item.name === "Celsius",
      );
    } else {
      from_unit_info = modbusStore.guiUnits.find(
        (item) => item.Symbol === from_unit_symbol?.device_unit,
      );
    }
    if (from_unit_info !== undefined && to_unit_info !== undefined) {
      fieldValue.value = modbusStore.convert(
        Number(value_org),
        from_unit_info.name,
        to_unit_info.name,
      );
      unit_conv_enable = true;
      unit_conv_org = from_unit_info.name;
      unit_conv_now = to_unit_info.name;
    } else {
      fieldValue.value = value_org;
    }
  }
  if (fullNumber) {
    fieldValue.value = fieldValue.value.toString();
  } else if (versionNumber) {
    fieldValue.value = decArrayToVersionString(
      bcdWord32ToDecArray(Number(fieldValue.value)),
    );
  } else {
    fieldValue.value = roundExponential(Number(fieldValue.value));
  }
}

function reloadValue() {
  reloadCount = 0;
  reload();
}

function reload() {
  if (modbusStore.storeInit && modbusStore.portState === PortState.connected) {
    if (modbusReqTimer.value !== 0) {
      clearInterval(modbusReqTimer.value);
    }
    //-------- Device Info -------------------------
    modbusStore.modbusRead(
      modbusStore.deviceAddress,
      parameter[ParamField.mbusAddress],
      modbusStore.modbusAddrLength[parameter[ParamField.mbusAddress]],
    );
  }
}

function writeModbus() {
  modbusStore.modbusWrite(
    modbusStore.deviceAddress,
    parameter[ParamField.mbusAddress],
    modbusStore.modbusAddrLength[parameter[ParamField.mbusAddress]],
    new Uint8Array(writeValueArray),
  );
}

function writeValue(write_value_x: string | number) {
  const write_value =
    typeof write_value_x === "string"
      ? parseFloat(write_value_x)
      : write_value_x;
  if (prevValue.value != write_value) {
    modbusStore.modbusIsChanged = false;
    let write_value_org;
    if (unit_conv_enable) {
      write_value_org = modbusStore.convert(
        write_value,
        unit_conv_now,
        unit_conv_org,
      );
    } else {
      write_value_org = write_value;
    }

    if (parameter.type === "uint16_t") {
      const bytes_value = uint16_to_bytes(write_value_org);
      writeValueArray = Array.from(bytes_value);
      writeModbus();
    } else if (
      parameter.type === "uint32_t" ||
      parameter.type === "float" ||
      parameter.type === "int"
    ) {
      let bytes: Uint8Array;
      if (parameter.type === "uint32_t") {
        bytes = uint32_to_bytes(write_value_org);
      } else if (parameter.type === "float") {
        bytes = float_to_bytes(write_value_org);
      } else {
        // int32
        bytes = int32_to_bytes(write_value_org);
      }
      writeValueArray = Array.from(bytes);
      writeModbus();
    }
  }
}

function setDefault() {
  writeValue(parameter.default);
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key.toLocaleLowerCase() === "enter") {
    if (fieldValue.value) {
      writeValue(fieldValue.value);
    }
  } else if (e.key.toLocaleLowerCase() === ".") {
    if (parameter.type !== "float") {
      e.preventDefault();
    }
  } else if (e.key.toLocaleLowerCase().match(/^[a-df-zA-DF-Z]$/)) {
    e.preventDefault();
  }
}

function onBlur(): void {
  if (isReadOnly.value) {
    return;
  }
  if (typeof fieldValue.value === "string") {
    fieldValue.value = fieldValue.value.replace(",", ".");
  }
  if (!fieldValue.value) {
    getValue();
    return;
  }
  if (versionNumber) {
    writeValue(
      decArrayToBcdWord32(versionStringToDecArray(String(fieldValue.value))),
    );
  } else {
    writeValue(fieldValue.value);
  }
}

watch(modbusAddrReadBusy, (newVal) => {
  if (!newVal) {
    if (modbusStore.modbusAddrErrMsg[parameter[ParamField.mbusAddress]] == "") {
      reloadCount = 0;
    } else {
      if (reloadCount < modbusStore.maxReloadCount) {
        reload();
        reloadCount++;
      }
    }
  }
});

watch(modbusAddrWriteBusy, (newVal: boolean, oldVal?: boolean) => {
  if (newVal === false && oldVal !== undefined && writeValueArray.length > 0) {
    if (modbusStore.modbusAddrErrMsg[parameter[ParamField.mbusAddress]] == "") {
      writeCount = 0;
    } else {
      if (writeCount < modbusStore.maxReloadCount) {
        writeModbus();
        writeCount++;
      }
    }
  }
});

watch(showUnit, () => {
  getValue();
});

watch(isLoading, (newVal) => {
  if (!newVal) {
    updateValue();
  }
});

onMounted(() => {
  if (parameter.type === "uint16_t") {
    modbusStore.modbusAddrLength[parameter[ParamField.mbusAddress]] = 1;
  } else if (
    parameter.type === "uint32_t" ||
    parameter.type === "float" ||
    parameter.type === "int"
  ) {
    modbusStore.modbusAddrLength[parameter[ParamField.mbusAddress]] = 2;
  }
  if (modbusReload) {
    modbusReqTimer.value = setInterval(() => reloadValue(), 200);
  } else {
    updateValue();
  }
});

onUnmounted(() => {
  if (modbusReqTimer.value) {
    clearInterval(modbusReqTimer.value);
  }
});
</script>
